import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlined from '@mui/icons-material/ErrorOutlined';
import Box from '@mui/material/Box';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { bigNumberWithCommas } from '@openx/utils/lib/bigNumberWithCommas.ts';
import { type JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getDataTypeLabels } from './constants.ts';
import type { UploadFileSuccessSummaryProps } from './types.ts';

const RecordsStatusTypography = (props: TypographyProps) => (
  <Typography
    component="span"
    {...props}
    sx={[
      {
        display: 'flex',
        alignItems: 'center',
      },
      ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ]}
  />
);
const RecordsNumberTypography = (props: TypographyProps) => (
  <Typography variant="body1Bold" component="span" {...props} />
);

export function UploadFileSummary(props: UploadFileSuccessSummaryProps): JSX.Element {
  const { t } = useTranslation();

  const dataTypeLabels = useMemo(() => getDataTypeLabels(t), [t]);

  const { dataType, recordsProcessed, recordsFailed } = props;

  const recordsProcessedSuccesfully = recordsProcessed - recordsFailed;
  const dataTypeLabel = dataTypeLabels[dataType];

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
      }}
    >
      <Typography variant="body1Bold" data-test="upload-summary">
        The summary of the operation is presented below.
      </Typography>
      <Typography data-test="upload-summary">
        <RecordsNumberTypography>{bigNumberWithCommas(recordsProcessed)}</RecordsNumberTypography>
        &nbsp;{dataTypeLabel} records processed
      </Typography>
      <Box>
        <RecordsStatusTypography data-test="upload-summary">
          <CheckCircleOutlined data-test="success-icon" color="success" fontSize="small" sx={{ marginRight: 1 }} />
          <RecordsNumberTypography sx={{ display: 'flex', alignItems: 'center' }}>
            {bigNumberWithCommas(recordsProcessedSuccesfully)} records
          </RecordsNumberTypography>
          &nbsp;uploaded successful
        </RecordsStatusTypography>
        <RecordsStatusTypography data-test="upload-summary">
          <ErrorOutlined color="error" data-test="error-icon" fontSize="small" sx={{ marginRight: 1 }} />
          <RecordsNumberTypography>{bigNumberWithCommas(recordsFailed)} records</RecordsNumberTypography>
          &nbsp;skipped due to invalid data
        </RecordsStatusTypography>
      </Box>
    </Box>
  );
}
